import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Users,
  RefreshCw,
  Loader2,
  Edit,
  Shield,
  Trash2,
  Eye
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-800' },
  { value: 'supervisor', label: 'Supervisor', color: 'bg-blue-100 text-blue-800' },
  { value: 'verification_agent', label: 'Verification Agent', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'fraud_analyst', label: 'Fraud Analyst', color: 'bg-orange-100 text-orange-800' },
  { value: 'auditor', label: 'Auditor', color: 'bg-slate-100 text-slate-800' },
];

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const { user: currentUser, startImpersonation, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [supervisors, setSupervisors] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'verification_agent',
    country_scope: [],
    password: '',
    supervisor_id: '',
    notify_on_new_case: true
  });

  // Fetch supervisors list
  const fetchSupervisors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/supervisors`);
      setSupervisors(response.data);
    } catch (error) {
      console.error('Failed to fetch supervisors:', error);
    }
  };

  // Fetch countries from policies
  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/policies`);
      const countries = response.data.map(policy => ({
        value: policy.country_code,
        label: policy.country_name
      }));
      setCountryOptions(countries);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      // Fallback to default countries if API fails
      setCountryOptions([
        { value: 'NG', label: 'Nigeria' },
        { value: 'KE', label: 'Kenya' },
        { value: 'GH', label: 'Ghana' },
        { value: 'ZA', label: 'South Africa' },
        { value: 'NL', label: 'Netherlands' },
      ]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCountries();
    fetchSupervisors();
  }, []);

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'verification_agent',
      country_scope: [],
      password: '',
      supervisor_id: '',
      notify_on_new_case: true
    });
    setEditingUser(null);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        country_scope: user.country_scope || [],
        password: '',
        supervisor_id: user.supervisor_id || '',
        notify_on_new_case: user.notify_on_new_case !== false
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCountryToggle = (country) => {
    setFormData(prev => ({
      ...prev,
      country_scope: prev.country_scope.includes(country)
        ? prev.country_scope.filter(c => c !== country)
        : [...prev.country_scope, country]
    }));
  };

  const handleAllCountriesToggle = () => {
    // "All Countries" is the backend's unrestricted scope: an EMPTY list. Picking
    // it clears any specific countries, so the reviewer is notified for every
    // country, including ones added later. To restrict, tick specific countries
    // below (which clears "All Countries").
    setFormData(prev => ({ ...prev, country_scope: [] }));
  };

  // An empty scope means "all countries" everywhere in the backend (the new-case
  // notification and the case-list filter both treat it that way).
  const isAllCountriesSelected = () => formData.country_scope.length === 0;

  const handleSubmit = async () => {
    if (!editingUser && (!formData.email || !formData.name || !formData.password)) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!editingUser && formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingUser) {
        await axios.patch(`${API_URL}/api/users/${editingUser.id}`, {
          role: formData.role,
          country_scope: formData.country_scope,
          supervisor_id: formData.supervisor_id || '',
          notify_on_new_case: formData.notify_on_new_case
        });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API_URL}/api/users`, {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          country_scope: formData.country_scope,
          password: formData.password,
          supervisor_id: formData.supervisor_id || null,
          notify_on_new_case: formData.notify_on_new_case
        });
        toast.success('User created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchUsers();
      fetchSupervisors(); // Refresh supervisors list in case roles changed
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleColor = (role) => {
    const option = ROLE_OPTIONS.find(r => r.value === role);
    return option?.color || 'bg-slate-100 text-slate-800';
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleDeleteUser = async (userId, userEmail) => {
    try {
      setDeletingId(userId);
      await axios.delete(`${API_URL}/api/users/${userId}`);
      toast.success(`User ${userEmail} deleted successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="users" />
          <Button variant="outline" size="sm" onClick={fetchUsers}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => handleOpenDialog()} data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogDescription>
                  {editingUser 
                    ? 'Update user role and permissions' 
                    : 'Create a new user account'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {!editingUser && (
                  <>
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input 
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        placeholder="john@edomatch.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input 
                        type="password"
                        placeholder="Minimum 8 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(v) => setFormData({ ...formData, role: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Show supervisor selection for roles that can have supervisors */}
                {formData.role && !['admin', 'supervisor'].includes(formData.role) && (
                  <div className="space-y-2">
                    <Label>Supervisor (Optional)</Label>
                    <Select 
                      value={formData.supervisor_id || "none"} 
                      onValueChange={(v) => setFormData({ ...formData, supervisor_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a supervisor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No supervisor assigned</SelectItem>
                        {supervisors
                          .filter(s => s.id !== editingUser?.id) // Can't be your own supervisor
                          .map((supervisor) => (
                            <SelectItem key={supervisor.id} value={supervisor.id}>
                              {supervisor.name} ({supervisor.role})
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Cases escalated by this user will be sent to their supervisor
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Country Access</Label>
                  <div className="space-y-3">
                    {/* All Countries option */}
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Checkbox 
                        id="all-countries"
                        checked={isAllCountriesSelected()}
                        onCheckedChange={handleAllCountriesToggle}
                      />
                      <label htmlFor="all-countries" className="text-sm font-medium">
                        All Countries
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground -mt-1">
                      {isAllCountriesSelected()
                        ? 'Notified for cases from every country. Tick specific countries below to restrict.'
                        : 'Restricted to the countries ticked below.'}
                    </p>
                    
                    {/* Individual countries */}
                    <div className="grid grid-cols-2 gap-2">
                      {countryOptions.map((country) => (
                        <div key={country.value} className="flex items-center gap-2">
                          <Checkbox 
                            id={country.value}
                            checked={formData.country_scope.includes(country.value)}
                            onCheckedChange={() => handleCountryToggle(country.value)}
                          />
                          <label htmlFor={country.value} className="text-sm">
                            {country.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>New Case Notifications</Label>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="notify-on-new-case"
                      checked={formData.notify_on_new_case}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, notify_on_new_case: checked === true })
                      }
                    />
                    <label htmlFor="notify-on-new-case" className="text-sm">
                      Email this reviewer when a new case is created in their country scope
                    </label>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingUser ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {ROLE_OPTIONS.slice(0, 4).map((role) => {
          const count = users.filter(u => u.role === role.value).length;
          return (
            <Card key={role.value}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{role.label}s</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={cn("p-2 rounded-lg", role.color.replace('text-', 'bg-').split(' ')[0])}>
                    <Users className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : users.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Supervisor</TableHead>
                  <TableHead>Country Access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-slate-100 text-sm">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn(getRoleColor(user.role))}>
                        {user.role.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.supervisor_name ? (
                        <span className="text-sm">{user.supervisor_name}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.country_scope?.slice(0, 3).map((c) => (
                          <Badge key={c} variant="outline" className="text-xs">
                            {c}
                          </Badge>
                        ))}
                        {user.country_scope?.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{user.country_scope.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={user.is_active ? "default" : "secondary"}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {user.requires_2fa && (
                          <Shield className="h-4 w-4 text-blue-500" title="2FA Required" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {/* Impersonate Button - Only for admins, not for self */}
                        {currentUser?.role === 'admin' && currentUser?.id !== user.id && !isImpersonating && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={async () => {
                              setImpersonatingId(user.id);
                              try {
                                await startImpersonation(user.id);
                                toast.success(`Now viewing as ${user.name}`, {
                                  description: 'You are in read-only mode. No changes can be made.',
                                  duration: 5000
                                });
                                navigate('/');
                              } catch (err) {
                                toast.error(err.message || 'Failed to start impersonation');
                              } finally {
                                setImpersonatingId(null);
                              }
                            }}
                            disabled={impersonatingId === user.id}
                            title={`View as ${user.name}`}
                            data-testid={`impersonate-user-${user.id}`}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            {impersonatingId === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(user)} title="Edit user">
                          <Edit className="h-4 w-4" />
                        </Button>
                        {currentUser?.id !== user.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={deletingId === user.id}
                                title="Delete user"
                                data-testid={`delete-user-${user.id}`}
                              >
                                {deletingId === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete <strong>{user.name}</strong> ({user.email}). 
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteUser(user.id, user.email)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-lg font-medium">No users found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add team members to get started
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default UserManagement;
